import { Button, Collapse, Form, Radio, Switch } from 'antd'
import { useContext, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useAuthContext } from '@/auth/authContext'
import { Skeleton } from '@/components/Skeleton'
import { useGetPacketOrForm } from '@/hooks/useGetPacketOrForm'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { CompletePageType } from '@/pages/Contexts/FormBuilderContext'
import { useDeleteCompletePageConditionalRuleById } from '@/queries/questions/useDeleteCompletePageConditionalRuleById'
import {
  invalidateLoadCompletePageConditionalRule,
  useLoadCompletePageConditionalRule,
} from '@/queries/questions/useLoadCompletePageConditionalRule'
import Tooltip from '@/ui/atoms/Tooltip'
import FormAntd from '@/ui/form/antd/FormAntd'
import { transformFromCompleteConditionalRuleResponse } from '@/utils/conditionalCompletePage/transformFromCompleteConditionalRuleResponse'
import { transformFromCompletePageStateToForm } from '@/utils/conditionalCompletePage/transformFromCompletePageStateToForm'
import { transformToCompleteConditionalRuleForm } from '@/utils/conditionalCompletePage/transformToCompleteConditionalRuleForm'
import { transformToCompletePageConditionalRuleModel } from '@/utils/conditionalCompletePage/transformToCompletePageConditionalRuleModel'
import { ZentakeContext } from '@/utils/ZentakeContext'

import { CompletePageConsumerModel } from '../CustomCompletePage'
import {
  editConditionalRuleInitializeData,
  editConditionalRuleItemExists,
} from '../UtilsCompletePage/editConditionalRuleHelper'
import { CollapseConditionalRule } from './CollapseConditionalRule'
import { AddConditionCompletePageModal } from './ConditionCompletePage'
import { EditConditionCompletePageModal } from './ConditionCompletePage/EditConditionCompletePageModal'
import { FormAddConditionCompletePageModel } from './ConditionCompletePage/useAddConditionCompletePageForm'
import { CustomPageEditor } from './CustomPageEditor'
import { DefaultCompletePageResponseForm } from './DefaultCompletePageResponseForm/DefaultCompletePageResponseForm'
import {
  CustomCompletePageModel,
  useCustomCompletePageForm,
} from './useCustomCompletePageForm'

export const CustomCompletePageForm = ({
  completePage,
  setCompletePage,
  isLoading,
}: CompletePageConsumerModel) => {
  const history = useHistory()
  const { teamDetails } = useContext(ZentakeContext)
  const { form: customCompletePageForm, rule } = useCustomCompletePageForm()

  const { user } = useAuthContext()
  const updateParams = useUpdateURLSearchParams()
  const searchParams = useSearchParams()
  const { form, packet } = useGetPacketOrForm()
  const { deleteCompletePageConditionalRuleById } =
    useDeleteCompletePageConditionalRuleById()

  const enableRequest = Boolean(completePage?.hasRedirectRules)

  const {
    data: conditionalRuleData,
    isLoading: isLoadingCompletePage,
    isFetching,
  } = useLoadCompletePageConditionalRule(
    { form, packet },
    { enabled: enableRequest }
  )

  const isDocumentBuilder = history.location.pathname.includes('/pdf/')
  const showConditionRedirectOption =
    teamDetails?.condition_form_complete && !isDocumentBuilder

  const bothConditionalRuleData = useMemo(() => {
    const conditionalRuleState = transformToCompleteConditionalRuleForm(
      completePage?.data,
      user
    )
    let bothRuleData = conditionalRuleData

    if (conditionalRuleState?.length) {
      bothRuleData = conditionalRuleState
    }
    return bothRuleData
  }, [completePage?.data, user, conditionalRuleData])

  const isOpenConditionSettings =
    searchParams.get('condition_settings') === 'true'
  const isOpenEditConditionSettings = searchParams.get(
    'edit_condition_settings'
  )

  const inviteTooltipTitle = {
    false: 'Click to active and customize the complete page',
    true: 'Click to disable the complete page',
  }

  const removeDataFromCompletePage = { ...completePage, data: undefined }

  const hasAnyRule = Object.values(removeDataFromCompletePage || {}).some(
    (value) => value
  )

  const toggleActiveCustomization = () => {
    setCompletePage?.((old) => {
      const removeData = { ...old, data: undefined }
      const hasAnyRule = Object.values(removeData || {}).some((value) => value)
      if (hasAnyRule) return undefined
      return {
        ...old,
        customCompletePage: old?.customCompletePage || 'default',
      }
    })
  }
  const getActiveCompletePageType = (
    completePage?: CompletePageType
  ): string | undefined => {
    if (!completePage) return undefined

    if (completePage.hasRedirectRules) return 'hasRedirectRules'
    if (completePage.customCompletePage) return 'customCompletePage'
    if (completePage.redirectUrl) return 'redirectUrl'

    return undefined
  }

  const handleCustomization = ({
    type,
    enter_url,
    custom_page,
  }: CustomCompletePageModel) => {
    setCompletePage?.((old) => ({
      ...old,
      redirectUrl: type === 'redirectUrl' ? enter_url || 'redirectUrl' : null,
      customCompletePage:
        type === 'customCompletePage' ? custom_page || 'default' : null,
      hasRedirectRules: type === 'hasRedirectRules',
    }))
  }

  const onDefaultResponse = ({
    type,
    enter_url,
    custom_page,
  }: CustomCompletePageModel) => {
    setCompletePage?.((old) => {
      let redirectUrl = null
      let customCompletePage = null
      if (type === 'custom_page') {
        customCompletePage = custom_page || 'default'
      }
      if (type === 'redirect_url') {
        redirectUrl = enter_url || 'redirect_url'
      }
      return {
        ...old,
        redirectUrl,
        customCompletePage,
      }
    })
  }

  const handleAddConditionalRule = (
    data: FormAddConditionCompletePageModel
  ) => {
    const newData = transformToCompletePageConditionalRuleModel(data, {
      formId: form,
      packetId: packet,
    })

    setCompletePage?.((old) => {
      const oldData = old?.data || []
      const lastItem = oldData.at(-1)
      const nextOrderNumber = Number(lastItem?.order || 0) + 1
      const hasMissingOrder = oldData.some((item) => item.order == null)

      if (!oldData.length) {
        const responseData =
          transformFromCompleteConditionalRuleResponse(conditionalRuleData) ||
          []

        const lastItemFromNewData = responseData.at(-1)
        const nextOrderNumberFromNewData =
          Number(lastItemFromNewData?.order || 0) + 1
        const hasMissingOrderFromNewData = responseData.some(
          (item) => item.order == null
        )
        if (hasMissingOrderFromNewData) {
          return {
            ...old,
            data: [
              ...responseData.map((item, index) => ({
                ...item,
                order: index + 1,
                is_updated: true,
              })),
              { ...newData, order: nextOrderNumberFromNewData },
            ],
          }
        }
        return {
          ...old,
          data: [
            ...responseData,
            { ...newData, order: nextOrderNumberFromNewData },
          ],
        }
      }
      if (hasMissingOrder) {
        return {
          ...old,
          data: [
            ...(old?.data || []).map((item, index) => ({
              ...item,
              order: index + 1,
              is_updated: true,
            })),
            { ...newData, order: nextOrderNumber },
          ],
        }
      }
      return {
        ...old,
        data: [...(old?.data || []), { ...newData, order: nextOrderNumber }],
      }
    })
    updateParams(['condition_settings', undefined], ['form_id', undefined])
  }

  const handleEditConditionalRule = (
    data: FormAddConditionCompletePageModel
  ) => {
    const editedData = transformToCompletePageConditionalRuleModel(data, {
      formId: form,
      packetId: packet,
    })

    setCompletePage?.((old) => {
      const oldData = old?.data || []
      const hasMissingOrder = oldData.some((item) => item.order == null)

      if (!oldData.length) {
        return editConditionalRuleInitializeData({
          conditionalRuleData,
          previousState: old,
          editedData,
          hasMissingOrder,
        })
      }

      const itemExists = oldData.some(
        (item) => String(item.id) === String(editedData.id)
      )
      if (itemExists) {
        return editConditionalRuleItemExists({
          previousState: old,
          oldData,
          editedData,
          hasMissingOrder,
        })
      }
      return {
        ...old,
        data: [...oldData, editedData],
      }
    })
    updateParams(['edit_condition_settings', undefined], ['form_id', undefined])
  }

  const confirmDeleteById = async (id: string, is_new?: boolean) => {
    try {
      if (!is_new) {
        await deleteCompletePageConditionalRuleById(id)
        await invalidateLoadCompletePageConditionalRule({ form, packet })
      }
      setCompletePage?.((old) => ({
        ...old,
        data: old?.data?.filter((item) => String(item.id) !== String(id)),
      }))
      toast.info('Rule removed')
    } catch (error) {
      toast.error('Error while removing rule')
    }
  }

  const initialValues = useMemo(() => {
    let internalInitialValues = transformFromCompletePageStateToForm(
      completePage?.data
    )

    if (!internalInitialValues?.length) {
      internalInitialValues =
        transformFromCompleteConditionalRuleResponse(conditionalRuleData)
    }
    return internalInitialValues
  }, [completePage, conditionalRuleData])

  useEffect(() => {
    const responseData =
      transformFromCompleteConditionalRuleResponse(conditionalRuleData) || []
    setCompletePage?.((old) => ({
      ...old,
      data: responseData,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionalRuleData, isFetching])

  return (
    <>
      <Collapse
        destroyInactivePanel
        activeKey={hasAnyRule ? ['1'] : []}
        className="bg-white [&_.ant-collapse-header:hover]:!bg-transparent [&_.ant-collapse-header]:!py-5"
        collapsible={hasAnyRule ? 'header' : 'disabled'}
        items={[
          {
            key: '1',
            label: (
              <div className="grid grid-cols-[1fr_50px] items-center gap-4">
                <div className="flex w-full flex-col">
                  <h2 className="-mt-0.5 text-lg font-medium text-gray-800">
                    Active Customization
                  </h2>
                  <p className="text-sm text-gray-500">
                    Choose what happens when someone completes this form
                  </p>
                </div>
              </div>
            ),
            extra: (
              <div className="mt-2 flex h-full items-center justify-center">
                <Tooltip
                  mouseEnterDelay={0.4}
                  title={
                    inviteTooltipTitle[
                      String(hasAnyRule) as keyof typeof inviteTooltipTitle
                    ]
                  }
                >
                  <i>
                    <Switch
                      checked={hasAnyRule}
                      className="scale-110"
                      onChange={toggleActiveCustomization}
                    />
                  </i>
                </Tooltip>
              </div>
            ),
            children: hasAnyRule ? (
              <FormAntd.Root<CustomCompletePageModel>
                form={customCompletePageForm}
                initialValues={{
                  type: getActiveCompletePageType(completePage),
                  enter_url: completePage.redirectUrl,
                }}
                layout="vertical"
                onValuesChange={(_, allValues) =>
                  handleCustomization(allValues)
                }
                validateTrigger={['onBlur']}
              >
                <div className="flex flex-col gap-2 px-2 pb-2 pt-3">
                  <Form.Item<CustomCompletePageModel>
                    name="type"
                    rules={[rule]}
                  >
                    <Radio.Group
                      options={[
                        {
                          value: 'customCompletePage',
                          label: 'Show Custom Page',
                        },
                        {
                          value: 'redirectUrl',
                          label: 'Redirect to External Link',
                        },
                        ...(showConditionRedirectOption
                          ? [
                              {
                                value: 'hasRedirectRules',
                                label: 'Conditional Redirect',
                              },
                            ]
                          : []),
                      ]}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                      }}
                    />
                  </Form.Item>
                  <Form.Item<CustomCompletePageModel>
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.type !== currentValues.type
                    }
                  >
                    {({ getFieldValue }) => {
                      const isCustomCompletePage =
                        getFieldValue('type') === 'customCompletePage'
                      const isRedirectUrl =
                        getFieldValue('type') === 'redirectUrl'
                      const isConditionalRedirect =
                        getFieldValue('type') === 'hasRedirectRules'
                      return (
                        <>
                          {isCustomCompletePage ? (
                            <div className="rounded border border-gray-300 bg-white">
                              <CustomPageEditor
                                {...{
                                  value: completePage?.customCompletePage,
                                  onChange: (value) =>
                                    setCompletePage((old) => ({
                                      ...old,
                                      customCompletePage: value,
                                    })),
                                }}
                              />
                            </div>
                          ) : null}
                          {isRedirectUrl ? (
                            <Form.Item
                              className="w-full [&_label]:!text-sm [&_label]:font-bold"
                              label="Enter URL"
                              name="enter_url"
                              rules={[rule]}
                            >
                              <FormAntd.Input
                                allowClear
                                placeholder="https://"
                                type="text"
                              />
                            </Form.Item>
                          ) : null}
                          {isConditionalRedirect ? (
                            <div className="mt-3 flex w-full flex-col">
                              <h2 className="text-lg font-bold">
                                Conditional Redirect
                              </h2>
                              <span className="mb-2 text-gray-500">
                                Direct to different destinations based on form
                                answers
                              </span>
                              <Button
                                htmlType="button"
                                onClick={() =>
                                  updateParams(['condition_settings', 'true'])
                                }
                                type="primary"
                              >
                                Add Condition
                              </Button>
                            </div>
                          ) : null}
                        </>
                      )
                    }}
                  </Form.Item>
                </div>
              </FormAntd.Root>
            ) : null,
          },
        ]}
      />
      {completePage?.hasRedirectRules ? (
        <div className="mt-4 flex flex-col gap-2">
          <h2 className="w-full text-lg font-medium">Conditional Rules</h2>
          <Skeleton
            {...{ isLoading: isLoadingCompletePage || isLoading }}
            className="!h-13"
          >
            {bothConditionalRuleData?.length ? (
              <CollapseConditionalRule
                conditionalRuleData={bothConditionalRuleData}
                {...{ confirmDeleteById }}
              />
            ) : (
              <span className="w-full text-gray-500">
                You haven’t added any conditions yet
              </span>
            )}
          </Skeleton>
          <Skeleton
            {...{ isLoading: isLoadingCompletePage || isLoading }}
            className="!h-13"
          >
            {bothConditionalRuleData?.length ? (
              <DefaultCompletePageResponseForm
                {...{ onDefaultResponse, completePage }}
              />
            ) : null}
          </Skeleton>
        </div>
      ) : null}

      {isOpenConditionSettings ? (
        <AddConditionCompletePageModal onFinish={handleAddConditionalRule} />
      ) : null}
      {isOpenEditConditionSettings ? (
        <EditConditionCompletePageModal
          initialValues={initialValues}
          onFinish={handleEditConditionalRule}
        />
      ) : null}
    </>
  )
}
